import {
  Bell,
  Bookmark,
  Compass,
  Gift,
  Grid3x3,
  Heart,
  Home,
  Image,
  List,
  MapPin,
  Menu,
  MessageCircle,
  Package,
  Search,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Star,
  Tag,
  User,
  type LucideIcon,
} from 'lucide-react';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

export interface OnboardingIconCatalogEntry {
  key: string;
  label: string;
  Icon: LucideIcon;
  svg: string;
}

// Curated subset of lucide icons for the onboarding tab bar builder, mirroring
// the `iconKey`-based lookup shape used by BottomTabButtonConfig in mobile/.
const CATALOG_SOURCE: { key: string; label: string; Icon: LucideIcon }[] = [
  { key: 'home', label: 'Início', Icon: Home },
  { key: 'cart', label: 'Carrinho', Icon: ShoppingCart },
  { key: 'search', label: 'Buscar', Icon: Search },
  { key: 'heart', label: 'Favoritos', Icon: Heart },
  { key: 'user', label: 'Perfil', Icon: User },
  { key: 'grid', label: 'Categorias', Icon: Grid3x3 },
  { key: 'star', label: 'Destaques', Icon: Star },
  { key: 'bell', label: 'Notificações', Icon: Bell },
  { key: 'tag', label: 'Ofertas', Icon: Tag },
  { key: 'gift', label: 'Presentes', Icon: Gift },
  { key: 'list', label: 'Lista', Icon: List },
  { key: 'package', label: 'Pedidos', Icon: Package },
  { key: 'bag', label: 'Sacola', Icon: ShoppingBag },
  { key: 'compass', label: 'Explorar', Icon: Compass },
  { key: 'menu', label: 'Menu', Icon: Menu },
  { key: 'settings', label: 'Configurações', Icon: Settings },
  { key: 'location', label: 'Lojas', Icon: MapPin },
  { key: 'chat', label: 'Suporte', Icon: MessageCircle },
  { key: 'bookmark', label: 'Salvos', Icon: Bookmark },
  { key: 'image', label: 'Galeria', Icon: Image },
];

export const ONBOARDING_ICON_CATALOG: OnboardingIconCatalogEntry[] =
  CATALOG_SOURCE.map(({ key, label, Icon }) => ({
    key,
    label,
    Icon,
    svg: renderToStaticMarkup(createElement(Icon)),
  }));

/**
 * Icon for a newly added custom tab: a random catalog entry that no other tab
 * already uses, so a fresh tab never looks like a duplicate of an existing one.
 * Falls back to the whole catalog (repeats allowed) only once every icon is in
 * use, which can't happen today (the tab limit is well under the catalog size).
 * `random` is injectable so callers/tests can make the pick deterministic.
 */
export function pickIconForNewTab(
  usedIconKeys: ReadonlySet<string>,
  catalog: readonly OnboardingIconCatalogEntry[] = ONBOARDING_ICON_CATALOG,
  random: () => number = Math.random
): OnboardingIconCatalogEntry {
  const unused = catalog.filter((entry) => !usedIconKeys.has(entry.key));
  const pool = unused.length > 0 ? unused : catalog;
  const picked = pool[Math.floor(random() * pool.length)];
  if (picked === undefined) {
    throw new Error('Cannot pick an icon from an empty catalog');
  }
  return picked;
}
